import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardMedia,
    Grid,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Alert,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    CloudUpload as UploadIcon,
    DragIndicator as DragIcon,
} from '@mui/icons-material';
import { useNotify } from 'react-admin';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API_URL } from '../config';

const ImageManager = ({ productId }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadDialog, setUploadDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isPrimary, setIsPrimary] = useState(false);
    const [addWatermark, setAddWatermark] = useState(true);
    const [removeBackground, setRemoveBackground] = useState(true);
    const notify = useNotify();

    const token = localStorage.getItem('token');

    // Fetch images for the product
    const fetchImages = React.useCallback(async () => {
        if (!productId) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/images/products/${productId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setImages(data.images || []);
            } else {
                notify('Failed to fetch images', { type: 'error' });
            }
        } catch (error) {
            notify('Error fetching images', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [productId, token, notify]);

    // Upload image
    const handleUpload = async () => {
        if (!selectedFile || !productId) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', selectedFile);
            formData.append('is_primary', isPrimary.toString());
            formData.append('add_watermark', addWatermark.toString());
            formData.append('remove_background', removeBackground.toString());

            const response = await fetch(`${API_URL}/images/products/${productId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (response.ok) {
                notify('Image uploaded successfully', { type: 'success' });
                setUploadDialog(false);
                setSelectedFile(null);
                setIsPrimary(false);
                setAddWatermark(true);
                setRemoveBackground(true);
                fetchImages();
            } else {
                const error = await response.json();
                notify(error.error || 'Failed to upload image', { type: 'error' });
            }
        } catch (error) {
            notify('Error uploading image', { type: 'error' });
        } finally {
            setUploading(false);
        }
    };

    // Delete image
    const handleDelete = async (imageId) => {
        if (!window.confirm('Are you sure you want to delete this image?')) return;

        try {
            const response = await fetch(`${API_URL}/images/${imageId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                notify('Image deleted successfully', { type: 'success' });
                fetchImages();
            } else {
                notify('Failed to delete image', { type: 'error' });
            }
        } catch (error) {
            notify('Error deleting image', { type: 'error' });
        }
    };

    // Set primary image
    const handleSetPrimary = async (imageId) => {
        try {
            const response = await fetch(`${API_URL}/images/${imageId}/primary`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                notify('Primary image updated', { type: 'success' });
                fetchImages();
            } else {
                notify('Failed to set primary image', { type: 'error' });
            }
        } catch (error) {
            notify('Error setting primary image', { type: 'error' });
        }
    };

    // Handle drag and drop reordering
    const handleSortEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const oldIndex = images.findIndex(image => image.ID === active.id);
        const newIndex = images.findIndex(image => image.ID === over.id);

        const sortedImages = arrayMove(images, oldIndex, newIndex);
        
        // Update local state immediately for better UX
        setImages(sortedImages);

        // Prepare ordering updates for backend
        const orderUpdates = sortedImages.map((image, index) => ({
            image_id: image.ID,
            ordering: index + 1
        }));

        try {
            const response = await fetch(`${API_URL}/images/products/${productId}/ordering`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(orderUpdates),
            });

            if (response.ok) {
                notify('Image order updated', { type: 'success' });
                // Refresh to get the updated ordering from server
                fetchImages();
            } else {
                notify('Failed to update image order', { type: 'error' });
                // Revert local changes on failure
                fetchImages();
            }
        } catch (error) {
            notify('Error updating image order', { type: 'error' });
            // Revert local changes on failure
            fetchImages();
        }
    };

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    if (!productId) {
        return (
            <Alert severity="info">
                Save the product first to manage images.
            </Alert>
        );
    }

    // Sortable Image Item Component
    const SortableImageItem = ({ image, onDelete, onSetPrimary }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({ id: image.ID });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
        };

        return (
            <Card 
                ref={setNodeRef} 
                style={style} 
                {...attributes}
                sx={{ 
                    position: 'relative',
                    border: image.is_primary ? '3px solid #1976d2' : '1px solid #e0e0e0',
                }}
            >
                <Box
                    {...listeners}
                    sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        cursor: 'grab',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        borderRadius: '4px',
                        padding: '2px',
                        zIndex: 1,
                    }}
                >
                    <DragIcon sx={{ color: 'white', fontSize: 16 }} />
                </Box>
                
                {image.is_primary && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: '#1976d2',
                            borderRadius: '50%',
                            padding: '2px',
                            zIndex: 1,
                        }}
                    >
                        <StarIcon sx={{ color: 'white', fontSize: 16 }} />
                    </Box>
                )}

                <CardMedia
                    component="img"
                    height="140"
                    image={image.url}
                    alt="Product image"
                    sx={{ objectFit: 'cover' }}
                />
                
                <CardActions sx={{ padding: 1, justifyContent: 'space-between' }}>
                    <Tooltip title={image.is_primary ? "Already primary" : "Set as primary"}>
                        <span>
                            <IconButton
                                onClick={() => onSetPrimary(image.ID)}
                                disabled={image.is_primary}
                                color="primary"
                                size="small"
                            >
                                {image.is_primary ? <StarIcon /> : <StarBorderIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    
                    <IconButton
                        onClick={() => onDelete(image.ID)}
                        color="error"
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>
                </CardActions>
            </Card>
        );
    };

    // Sortable Container
    const SortableImageGrid = ({ images, onSetPrimary, onDelete }) => {
        const sensors = useSensors(
            useSensor(KeyboardSensor, {
                coordinateGetter: sortableKeyboardCoordinates,
            }),
            useSensor(PointerSensor, {
                activationConstraint: {
                    distance: 10,
                },
            })
        );

        return (
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSortEnd}
            >
                <SortableContext items={images.map(image => image.ID)} strategy={rectSortingStrategy}>
                    <Grid container spacing={2}>
                        {images.map((image, index) => (
                            <Grid item xs={12} sm={6} md={4} key={image.ID}>
                                <SortableImageItem
                                    image={image}
                                    onSetPrimary={onSetPrimary}
                                    onDelete={onDelete}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </SortableContext>
            </DndContext>
        );
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Product Images</Typography>
                <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={() => setUploadDialog(true)}
                >
                    Upload Image
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {images.length === 0 ? (
                        <Alert severity="info">
                            No images uploaded yet. Click "Upload Image" to add product images.
                        </Alert>
                    ) : (
                        <SortableImageGrid
                            images={images}
                            onSetPrimary={handleSetPrimary}
                            onDelete={handleDelete}
                        />
                    )}
                </>
            )}

            {/* Upload Dialog */}
            <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Product Image</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => setSelectedFile(e.target.files[0])}
                            style={{ marginBottom: '16px' }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isPrimary}
                                    onChange={(e) => setIsPrimary(e.target.checked)}
                                />
                            }
                            label="Set as primary image"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={addWatermark}
                                    onChange={(e) => setAddWatermark(e.target.checked)}
                                />
                            }
                            label="Add watermark"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={removeBackground}
                                    onChange={(e) => setRemoveBackground(e.target.checked)}
                                />
                            }
                            label="Remove background (set to white)"
                        />
                        {selectedFile && (
                            <Typography variant="body2" color="text.secondary">
                                Selected: {selectedFile.name}
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleUpload}
                        variant="contained"
                        disabled={!selectedFile || uploading}
                    >
                        {uploading ? <CircularProgress size={20} /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ImageManager; 